import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { RESOURCE_PATH, TRACK_EVENT_SOURCES } from 'shared/consts';
import { push } from 'store/router/actions';
import { resetResource } from 'store/resource/actions';
import { resetResourceTimeRanges } from 'store/resource-time-ranges/actions';
import { FloorplanPlacement } from 'floorplan/pages/native-floorplan/types';
import { useCoworkerVisibility } from 'floorplan/hooks/use-coworker-visibility.hook';
import { getPlacementStatus, isBookable, PLACEMENT_STATUS_LABEL_ID, PlacementStatus } from '../utils/placement-status';

/** The "booked by" line, ready for `intl.formatMessage` — which message depends on what is revealed. */
export interface PlacementHeldBy {
  messageId: 'bookedBy' | 'bookedByCompany' | 'bookedByWithCompany';
  values: { name?: string; company?: string };
}

interface UsePlacementDetailsValues {
  status: PlacementStatus;
  statusLabelId: string;
  isAvailable: boolean;
  /** Section names, already ordered — a placement can belong to more than one. */
  sectionNames: string[];
  /**
   * Who holds a booked desk (CV-FP-02): the name, the company, or both — whatever the building
   * reveals. Null for a free desk, the user's own, an anonymous holder, or a building revealing nothing.
   */
  heldBy: PlacementHeldBy | null;
  openResource: VoidFunction;
}

/**
 * Details of the placement the user tapped, and the way out of the map into the booking flow.
 *
 * Booking is not reimplemented here: the resource page already owns date/time selection, payment
 * and confirmation, so both the book and the view actions land there — the same thing the Archilogic
 * popup does. `source` tells analytics the booking started from the floorplan.
 */
export const usePlacementDetails = (placement: FloorplanPlacement | null): UsePlacementDetailsValues => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const { effectiveName, effectiveCompany } = useCoworkerVisibility();

  const openResource = useCallback(() => {
    if (!placement) {
      return;
    }

    // Built through URLSearchParams rather than string concatenation: with no query string of its
    // own, appending `&source=` would glue it onto the uuid and the route would never match.
    const params = new URLSearchParams(search);

    params.set('source', TRACK_EVENT_SOURCES.FLOOR_PLAN);

    // The resources list opens a desk with no date, and the resource page then selects the next
    // bookable window. The floorplan always has today in the url, so drop it unless a time was
    // already chosen. Clear the previous resource the same way a list click does.
    if (!params.get('startTime') || !params.get('endTime')) {
      params.delete('startDate');
      params.delete('startDates');
    }

    dispatch(resetResource());
    dispatch(resetResourceTimeRanges());
    dispatch(push(`${RESOURCE_PATH}/${placement.resource.uuid}?${params.toString()}`));
  }, [dispatch, placement, search]);

  return useMemo(() => {
    const status = placement ? getPlacementStatus(placement, { showCoworkerIdentity: effectiveName }) : 'available';

    return {
      status,
      statusLabelId: placement ? PLACEMENT_STATUS_LABEL_ID[status] : '',
      isAvailable: !!placement && isBookable(placement),
      sectionNames: placement?.sections.map(({ name }) => name) ?? [],
      heldBy: placement ? resolveHeldBy(placement, status, { effectiveName, effectiveCompany }) : null,
      openResource,
    };
  }, [placement, effectiveName, effectiveCompany, openResource]);
};

/**
 * The server strips fields the building hides, so what arrived is what may be shown — the config
 * checks on top only guard a cached response from an earlier configuration. A company-only building
 * never yields the `team` status (that needs a name to draw), yet its booked desks still say which
 * company holds them — so the line keys off "booked, not mine, has a booker", not off the status.
 */
const resolveHeldBy = (
  placement: FloorplanPlacement,
  status: PlacementStatus,
  { effectiveName, effectiveCompany }: { effectiveName: boolean; effectiveCompany: boolean },
): PlacementHeldBy | null => {
  const booker = placement.occupancy?.booker;

  if (!booker || status === 'mine' || status === 'available') {
    return null;
  }

  const name = effectiveName ? booker.name ?? undefined : undefined;
  const company = effectiveCompany ? booker.company ?? undefined : undefined;

  if (name && company) {
    return { messageId: 'bookedByWithCompany', values: { name, company } };
  }

  if (name) {
    return { messageId: 'bookedBy', values: { name } };
  }

  return company ? { messageId: 'bookedByCompany', values: { company } } : null;
};
